/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useDataFetchingClient, useQuery } from '../../utils/DataFetchingClient';
import { useCallback } from 'react';
import { getQueryFieldName, queryDataFromCache, updateCache } from '../../utils/DataFetchingClientCache.new';
import { useUserInfo } from '../accounts/accountsQueries';
import Raven from 'raven-js';
import { refetchMyPlaylists } from '../myPlaylists/myPlaylistsQueries';
import { useFilteredRestrictedPlaylistItems } from '../playlistItems/playlistItemsHooks';
import { GET_PLAYLIST_OR_LEARNING_PATH_QUERY } from 'academy-common-lib/__generated__/chirp/com/hubspot/learning/center/playlists/rpc/LearningPathChirpService.dfc.new';
import { GET_SIMPLE_LEARNING_PATHS_OR_PLAYLISTS_QUERY } from 'academy-common-lib/__generated__/chirp/com/hubspot/learning/center/playlists/rpc/LearningPathChirpService.dfc.new';
export { GET_PLAYLIST_OR_LEARNING_PATH_QUERY };
import { useCreatePlaylistMutation, useAddPlaylistItemMutation, useDeletePlaylistItemMutation, useUpdatePlaylistMutation } from 'academy-common-lib/__generated__/chirp/com/hubspot/learning/center/playlists/rpc/PlaylistChirpService.hooks';
export const updatePlaylistFromCache = (playlistId, playlist, client) => {
  const variables = {
    playlistId
  };
  const currentPlaylist = queryDataFromCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, variables, client);
  if (currentPlaylist) {
    const updatedPlaylist = playlist ? Object.assign({}, currentPlaylist, playlist) : null;
    updateCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, () => updatedPlaylist, variables, client);
  }
};
export const updatePlaylistItemsInCache = ({
  playlistId,
  items,
  client
}) => {
  const currentPlaylist = queryDataFromCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, {
    playlistId
  }, client);
  if (currentPlaylist && items) {
    const updatedPlaylist = Object.assign({}, currentPlaylist, {
      items
    });
    updateCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, () => updatedPlaylist, {
      playlistId
    }, client);
  }
};
export const usePlaylist = (playlistId, skip = false) => {
  const {
    data,
    loading: playlistLoading,
    error: playlistError
  } = useQuery(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, {
    variables: {
      playlistId
    },
    skip
  });
  return {
    playlist: data,
    playlistLoading,
    playlistError
  };
};

// the difference between a full playlist and a basic one is that the basic playlist
// has an empty list of items. The way to know if it is a full playlist
const isFullPlaylist = cachePlaylist => cachePlaylist && cachePlaylist.items && cachePlaylist.itemsCount === cachePlaylist.items.length;
export const useFullPlaylist = (playlistId, skip) => {
  const client = useDataFetchingClient();
  const cachePlaylist = queryDataFromCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, {
    playlistId
  }, client);
  const {
    data,
    loading: playlistLoading,
    error: playlistError
  } = useQuery(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, {
    variables: {
      playlistId
    },
    // in case we have the basic playlist (no items) in cache or it has not been cached
    // we want to request the full playlist and update the cache with it
    fetchPolicy: isFullPlaylist(cachePlaylist) ? 'cache-only' : 'network-only',
    skip
  });
  return {
    playlist: data,
    playlistLoading,
    playlistError,
    displayedPlaylistItems: useFilteredRestrictedPlaylistItems((data === null || data === void 0 ? void 0 : data.items) || [])
  };
};
export const useCreatePlaylist = ({
  onCompleted,
  onError
} = {}) => {
  const client = useDataFetchingClient();
  const [mutate, {
    loading: createPlaylistLoading,
    error: createPlaylistError
  }] = useCreatePlaylistMutation({
    onCompleted: data => {
      if (data !== null && data !== void 0 && data.result && onCompleted) {
        onCompleted(data.result);
      }
      // refetch doesn't work if we add an item to the recently created playlist.
      // Using manual eviction as an alternative
      refetchMyPlaylists(client);
    },
    onError
  });
  const createPlaylist = useCallback(({
    name,
    description,
    items
  }) => {
    return mutate({
      variables: {
        name,
        description,
        items
      }
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    createPlaylist,
    createPlaylistLoading,
    createPlaylistError
  };
};
export const useUpdatePlaylist = (playlistId, {
  onCompleted,
  refetchQueries,
  onError
} = {
  onCompleted: () => {},
  onError: () => {},
  refetchQueries: []
}) => {
  const client = useDataFetchingClient();
  const {
    playlist
  } = usePlaylist(playlistId);
  const [mutate, {
    loading: updatePlaylistMutationLoading,
    called: updatePlaylistMutationCalled,
    error: updatePlaylistMutationError
  }] = useUpdatePlaylistMutation({
    refetchQueries,
    onCompleted,
    onError,
    update(cache, result) {
      const {
        data,
        errors
      } = result;
      if (!data || !data.result || errors && errors.length) return;
      if (data.result) {
        updatePlaylistFromCache(data.result.id, data.result, client);
      }
      cache.evict({
        fieldName: getQueryFieldName(GET_SIMPLE_LEARNING_PATHS_OR_PLAYLISTS_QUERY)
      });
    }
  });
  const updatePlaylist = useCallback(updatedPlaylist => {
    if (!playlist) return Promise.resolve();
    const newPlaylist = Object.assign({
      items: playlist.items,
      hiddenForOwner: playlist.hiddenForOwner,
      name: playlist.name,
      description: playlist.description,
      thumbnailSquareUrl: playlist.thumbnailSquareUrl,
      thumbnailRectangleUrl: playlist.thumbnailRectangleUrl
    }, updatedPlaylist);
    return mutate({
      variables: {
        playlistId,
        playlistUpdateRequest: newPlaylist
      }
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate, playlist, playlistId]);
  return [updatePlaylist, {
    updatePlaylistMutationLoading,
    updatePlaylistMutationCalled,
    updatePlaylistMutationError
  }];
};
export const useAddToPlaylist = () => {
  const client = useDataFetchingClient();
  const [mutate, {
    loading: addToPlaylistMutationLoading,
    called: addToPlaylistMutationCalled,
    error: addToPlaylistMutationError
  }] = useAddPlaylistItemMutation({
    update: (cache, {
      data,
      errors
    }) => {
      if (!data || !data.result || errors && errors.length) return;
      updatePlaylistItemsInCache({
        playlistId: data.result.id,
        items: data.result.items,
        client
      });
    }
  });
  const addToPlaylist = useCallback(({
    playlistId,
    itemId,
    type,
    language
  }) => {
    return mutate({
      variables: {
        playlistId,
        playlistItemRequest: {
          itemId,
          type,
          language
        }
      }
    });
  }, [mutate]);
  return [addToPlaylist, {
    addToPlaylistMutationLoading,
    addToPlaylistMutationCalled,
    addToPlaylistMutationError
  }];
};
export const useRemoveFromPlaylist = () => {
  const client = useDataFetchingClient();
  const [mutate, {
    loading: removeFromPlaylistMutationLoading,
    called: removeFromPlaylistMutationCalled,
    error: removeFromPlaylistMutationError
  }] = useDeletePlaylistItemMutation({
    update: (_, {
      data,
      errors
    }) => {
      if (!data || !data.result || errors && errors.length) return;
      updatePlaylistItemsInCache({
        playlistId: data.result.id,
        items: data.result.items,
        client
      });
    }
  });
  const removeFromPlaylist = useCallback(({
    playlistId,
    itemId,
    type,
    language
  }) => {
    return mutate({
      variables: {
        playlistId,
        playlistItemRequest: {
          itemId,
          type,
          language
        }
      }
    });
  }, [mutate]);
  return [removeFromPlaylist, {
    removeFromPlaylistMutationLoading,
    removeFromPlaylistMutationCalled,
    removeFromPlaylistMutationError
  }];
};
export const useUserOwnsPlaylist = playlistOrPlaylistId => {
  const isPlaylist = playlistOrPlaylistId && typeof playlistOrPlaylistId === 'object';
  const playlistId = isPlaylist ? playlistOrPlaylistId.id : playlistOrPlaylistId;
  const {
    userInfo
  } = useUserInfo();
  const {
    playlist: playlistFromQuery
  } = usePlaylist(playlistId, !!(!playlistId || isPlaylist));
  const playlist = isPlaylist ? playlistOrPlaylistId : playlistFromQuery;
  if (!userInfo || !playlist) return null;
  return playlist.ownerUser.userId === userInfo.user.id;
};
export const useSetPlaylistThumbnail = playlistId => {
  const {
    playlist,
    playlistLoading
  } = usePlaylist(playlistId);
  const [mutate, {
    updatePlaylistMutationLoading: setPlaylistThumbnailMutationLoading,
    updatePlaylistMutationCalled: setPlaylistThumbnailMutationCalled,
    updatePlaylistMutationError: setPlaylistThumbnailMutationError
  }] = useUpdatePlaylist(playlistId);
  const setPlaylistThumbnail = useCallback((thumbnailRectangleUrl, thumbnailSquareUrl) => {
    return mutate({
      thumbnailRectangleUrl: thumbnailRectangleUrl || undefined,
      thumbnailSquareUrl: thumbnailSquareUrl || undefined,
      items: playlist === null || playlist === void 0 ? void 0 : playlist.items,
      hiddenForOwner: playlist === null || playlist === void 0 ? void 0 : playlist.hiddenForOwner,
      name: playlist === null || playlist === void 0 ? void 0 : playlist.name,
      description: playlist === null || playlist === void 0 ? void 0 : playlist.description
    });
  }, [mutate, playlist]);
  return [setPlaylistThumbnail, {
    setPlaylistThumbnailMutationLoading,
    setPlaylistThumbnailMutationCalled,
    setPlaylistThumbnailMutationError,
    playlistLoading
  }];
};